import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SensorEditOptions } from '../types';

interface SensorAssignmentFieldsProps {
  sensortype: string;
  usergroup: string;
  site: string;
  location: string;
  options: SensorEditOptions;
  isAdmin: boolean;
  onChange: (field: string, value: string) => void;
}

export function SensorAssignmentFields({
  sensortype,
  usergroup,
  site,
  location,
  options,
  isAdmin,
  onChange,
}: SensorAssignmentFieldsProps) {
  const filteredLocations = useMemo(() => {
    if (!site) return options.locations;
    return options.locations.filter((loc) => loc.site === site);
  }, [options.locations, site]);

  const handleSiteChange = (value: string) => {
    onChange('site', value);
    if (value && location) {
      const locationStillValid = options.locations.some(
        (loc) => loc._id === location && loc.site === value,
      );
      if (!locationStillValid) {
        onChange('location', '');
      }
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Assignment
      </h4>
      <div>
        <Label>Sensor Type</Label>
        <Select
          value={sensortype || '__NONE__'}
          onValueChange={(v) => onChange('sensortype', v === '__NONE__' ? '' : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select type..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__NONE__">—</SelectItem>
            {options.sensortypes.map((st) => (
              <SelectItem key={st._id} value={st._id}>
                {st.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>
          User Group{!isAdmin && <span className="text-destructive"> *</span>}
        </Label>
        <Select
          value={usergroup || '__NONE__'}
          onValueChange={(v) => onChange('usergroup', v === '__NONE__' ? '' : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select group..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__NONE__">—</SelectItem>
            {options.usergroups.map((ug) => (
              <SelectItem key={ug._id} value={ug._id}>
                {ug.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Site</Label>
        <Select
          value={site || '__NONE__'}
          onValueChange={(v) => handleSiteChange(v === '__NONE__' ? '' : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select site..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__NONE__">—</SelectItem>
            {options.sites.map((s) => (
              <SelectItem key={s._id} value={s._id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Location</Label>
        <Select
          value={location || '__NONE__'}
          onValueChange={(v) => onChange('location', v === '__NONE__' ? '' : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select location..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__NONE__">—</SelectItem>
            {filteredLocations.map((loc) => (
              <SelectItem key={loc._id} value={loc._id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
